// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Ball search and stuck-ball diagnostics (M2-20, PH-090 … PH-092).
//
// Real machines get stuck balls, and real machines solve them by pulsing coils until
// something shakes loose. Simulating the failure without simulating the recovery would be the
// worst of both worlds: a player whose ball dies in a pocket, with no machine noise, no
// escalation, and no ball back.
//
// The escalation is the real one, in order, each stage given time to work before the next:
//
//   1. Pulse the coils **near** the ball. Most stuck balls are resting on a bumper skirt or
//      against a sling, and one pulse of the right coil frees them.
//   2. Pulse **every** coil. The machine makes the noise every pinball player recognises.
//   3. A **bounded corrective impulse** away from whatever the ball is resting against. This
//      is the first stage that is not something a real machine can do, and it is bounded
//      precisely so it cannot be a scoring advantage.
//   4. Teleport to the trough and serve a **free ball with the bonus preserved**, which is
//      what a real machine does when its own search fails.
//
// Every stage is reported (PH-092) with the machine id and version, the ball's position and
// the collider it was resting against. That is the part that matters beyond this ball: it
// turns a player's annoyance into a machine-design bug report, and it is what keeps the
// Machine Builder's published content honest. A machine whose search log is full of the same
// coordinate has a pocket its author never found.

import type { World } from '../sim/World'
import type { Ball } from '../sim/Ball'
import { colliderContact, createContact, taperedCapsuleContact } from '../sim/colliders'
import { BALL_RADIUS, STEP_HZ } from '../sim/constants'
import { sqrt } from '../math/deterministic'
import type { MachineDefinition } from '../machine/types'

/** PH-090. Slower than this, for this long, outside a holding device, is suspect. */
export const SUSPECT_SPEED = 0.05
export const SUSPECT_SECONDS = 3

/** Time given to each stage before escalating. */
export const STAGE_SECONDS = 1.5

/** How far from the ball a coil counts as "nearby" for stage 1. */
export const NEARBY_RADIUS = 0.09

/** Stage 3's corrective impulse, m/s. Bounded so it can never be worth engineering. */
export const CORRECTIVE_SPEED = 0.55

export type SearchStage = 0 | 1 | 2 | 3 | 4

export interface BallSearchReport {
  readonly machineId: string
  readonly machineVersion: string
  readonly ballId: number
  readonly stage: SearchStage
  readonly x: number
  readonly y: number
  readonly step: number
  /** The collider the ball was resting against, if one could be identified. */
  readonly restingAgainst: string | undefined
  /** True only for stage 4 — the ball was given back rather than freed. */
  readonly teleported: boolean
}

export interface BallSearchState {
  slowSteps: number
  stage: SearchStage
  stageStep: number
}

export class BallSearch {
  private readonly world: World
  private readonly machineId: string
  private readonly machineVersion: string
  /** Collider index → the definition id that produced it, for the diagnostic. */
  private readonly colliderNames: string[]
  private readonly state: BallSearchState[] = []
  private readonly contact = createContact()
  private readonly suspectSteps = SUSPECT_SECONDS * STEP_HZ
  private readonly stageSteps = STAGE_SECONDS * STEP_HZ

  /** Where a recovered ball is served from. */
  troughX = 0
  troughY = 0

  readonly reports: BallSearchReport[] = []

  private readonly persistent: boolean
  private readonly protectCradles: boolean

  constructor(world: World, definition: MachineDefinition, colliderNames: string[] = []) {
    this.world = world
    this.persistent = definition.physics?.persistentBallSearch ?? false
    this.protectCradles = definition.physics?.flipperCradleProtection ?? false
    this.machineId = definition.machineId
    this.machineVersion = definition.machineVersion
    this.colliderNames = colliderNames
    for (let i = 0; i < world.balls.length; i++) {
      this.state.push({ slowSteps: 0, stage: 0, stageStep: 0 })
    }
  }

  /**
   * How close each ball is to being declared stuck (M4-28a).
   *
   * Restored across a chunk boundary because a ball that has been motionless for nine seconds is
   * one second from a coil pulse, and a resumed game that forgot would let it sit for another
   * ten. The reports are diagnostics rather than state and are deliberately not carried.
   */
  snapshot(): BallSearchState[] {
    return this.state.map((s) => ({ slowSteps: s.slowSteps, stage: s.stage, stageStep: s.stageStep }))
  }

  restore(states: readonly BallSearchState[]): void {
    if (states.length !== this.state.length) {
      throw new Error(`BallSearch.restore: ${states.length} balls, expected ${this.state.length}`)
    }
    for (const [i, s] of states.entries()) {
      this.state[i]!.slowSteps = s.slowSteps
      this.state[i]!.stage = s.stage
      this.state[i]!.stageStep = s.stageStep
    }
  }

  reset(): void {
    for (const s of this.state) {
      s.slowSteps = 0
      s.stage = 0
      s.stageStep = 0
    }
  }

  /**
   * Watch every ball for one step and escalate any that has stopped.
   *
   * Returns the coil ids the machine should pulse this step, so the caller decides what a
   * pulse actually does. The search never fires a coil itself, for the same reason the rule
   * engine does not: the thing that decides and the thing that acts stay separate.
   */
  advance(step: number, coilsToPulse: string[]): void {
    // By ball index, so two runtimes escalate two stuck balls in the same order.
    for (let i = 0; i < this.world.balls.length; i++) {
      const ball = this.world.balls[i]!
      const s = this.state[i]!

      // A ball a device is deliberately holding is not stuck. A scoop is supposed to keep it.
      if (!ball.active || ball.heldBy >= 0 || (this.protectCradles && this.isCradled(ball))) {
        s.slowSteps = 0
        s.stage = 0
        // Keep legacy snapshots exact. Protected cradles clear every timer so a release
        // cannot inherit a pending corrective impulse or teleport.
        if (this.protectCradles) s.stageStep = 0
        continue
      }

      if (ball.speed() >= SUSPECT_SPEED) {
        // New tables use a leaky quiet-time counter: a brief nudge cannot erase
        // three seconds of stillness. 750 ms of sustained motion clears it fully.
        s.slowSteps = this.persistent ? (s.slowSteps > 4 ? s.slowSteps - 4 : 0) : 0
        if (s.stage !== 0 && s.slowSteps === 0) s.stage = 0
        continue
      }

      if (!this.persistent || s.slowSteps < this.suspectSteps) s.slowSteps++
      if (s.slowSteps < this.suspectSteps && (!this.persistent || s.stage === 0)) continue

      if (s.stage === 0) {
        this.escalate(ball, s, step, coilsToPulse)
        continue
      }
      if (step - s.stageStep >= this.stageSteps) this.escalate(ball, s, step, coilsToPulse)
    }
  }

  /** Reuse the actual bat query with 1 mm solver clearance; no per-step allocation. */
  private isCradled(ball: Ball): boolean {
    if (ball.layer !== this.world.homePlane || Math.abs(ball.z - ball.radius) > 0.002 ||
        Math.abs(ball.vz) >= SUSPECT_SPEED || ball.speed() >= SUSPECT_SPEED) return false
    const x = ball.x - this.world.frameX
    const y = ball.y - this.world.frameY
    for (const f of this.world.flippers) {
      if (!f.pressed || f.strokeFraction() < 0.95 || Math.abs(f.omega) * f.length >= SUSPECT_SPEED) continue
      this.contact.hit = false
      if (taperedCapsuleContact(f.pivotX, f.pivotY, f.tipX, f.tipY,
          f.radiusPivot, f.radiusTip, x, y, ball.radius + 0.001, this.contact) &&
          this.contact.ny > 0.25) return true
    }
    return false
  }

  private escalate(ball: Ball, s: BallSearchState, step: number, coilsToPulse: string[]): void {
    s.stage = (s.stage + 1) as SearchStage
    s.stageStep = step

    const against = this.restingAgainst(ball)

    this.reports.push({
      machineId: this.machineId,
      machineVersion: this.machineVersion,
      ballId: ball.id,
      stage: s.stage,
      x: ball.x,
      y: ball.y,
      step,
      restingAgainst: against.name,
      teleported: s.stage === 4,
    })

    switch (s.stage) {
      case 1:
        for (const device of this.world.devices) {
          const dx = device.originX - ball.x
          const dy = device.originY - ball.y
          if (dx * dx + dy * dy <= NEARBY_RADIUS * NEARBY_RADIUS) coilsToPulse.push(device.id)
        }
        break
      case 2:
        for (const device of this.world.devices) coilsToPulse.push(device.id)
        break
      case 3: {
        // Push away from whatever it is resting on. If nothing was identified, push upfield,
        // which is the direction that gives the ball somewhere to go.
        const nx = against.nx
        const ny = against.ny
        ball.vx += nx * CORRECTIVE_SPEED
        ball.vy += ny * CORRECTIVE_SPEED
        ball.setRollingSpin()
        break
      }
      case 4:
        ball.reset(this.troughX, this.troughY, BALL_RADIUS)
        s.stage = 0
        s.slowSteps = 0
        break
    }
  }

  /**
   * Identify the surface the ball is against, and the direction away from it.
   *
   * Probes with an inflated radius so a ball resting *near* a collider is found, not only one
   * already interpenetrating it — a settled ball is usually a hair off the surface, held
   * there by the contact solver.
   */
  private restingAgainst(ball: Ball): { name: string | undefined; nx: number; ny: number } {
    const probe = ball.radius + 0.004
    let best = -1
    let bestDepth = -Infinity
    let nx = 0
    let ny = 1

    for (let i = 0; i < this.world.colliders.length; i++) {
      const collider = this.world.colliders[i]!
      if (collider.layer !== ball.layer) continue
      this.contact.hit = false
      if (!colliderContact(collider, ball.x, ball.y, probe, this.contact)) continue
      if (this.contact.depth <= bestDepth) continue
      bestDepth = this.contact.depth
      best = i
      nx = this.contact.nx
      ny = this.contact.ny
    }

    if (best < 0) return { name: undefined, nx: 0, ny: 1 }
    const len = sqrt(nx * nx + ny * ny)
    if (len === 0) return { name: this.colliderNames[best], nx: 0, ny: 1 }
    return { name: this.colliderNames[best], nx: nx / len, ny: ny / len }
  }
}
